import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { ExternalLink, ArrowLeft, ArrowDown } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import SEO from '../components/SEO';
import { trackAffiliateClick } from '../hooks/useGoogleAnalytics';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SITE_URL = 'https://uandfashion.eu';

const LookPage = () => {
  const { slug } = useParams();
  const { language, t } = useLanguage();
  const [look, setLook] = useState(null);
  const [status, setStatus] = useState('loading'); // loading | ok | notfound | error

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await axios.get(`${API}/looks/${slug}`);
        if (cancelled) return;
        setLook(res.data);
        setStatus('ok');
      } catch (err) {
        if (cancelled) return;
        setStatus(err?.response?.status === 404 ? 'notfound' : 'error');
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  const intro = look
    ? (language === 'nl' ? look.intro_nl || look.intro_en : look.intro_en || look.intro_nl)
    : '';

  if (status === 'loading') {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (status === 'notfound' || status === 'error') {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-6">
        <div className="text-center max-w-md">
          <h1 className="text-3xl font-light text-gray-900 mb-3">
            {t('Look niet gevonden', 'Look not found')}
          </h1>
          <p className="text-gray-600 mb-6">
            {t(
              'Deze look bestaat niet meer of is verwijderd.',
              'This look no longer exists or has been removed.'
            )}
          </p>
          <Link
            to="/instagram"
            className="inline-flex items-center gap-2 bg-gray-900 text-white px-6 py-3 rounded-full text-sm font-medium hover:bg-gray-800 transition-colors"
            data-testid="look-notfound-hub-link"
          >
            {t('Bekijk alle looks', 'Browse all looks')}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-b from-white via-pink-50/30 to-white" data-testid="look-page">
      <SEO
        title={`${look.title} — U&Fashion`}
        description={intro || t(
          'Ontdek deze look en shop direct bij onze partner-merken.',
          'Discover this look and shop directly at our partner brands.'
        )}
        canonical={`${SITE_URL}/look/${look.slug}`}
        ogImage={look.hero_image}
      />

      <div className="max-w-4xl mx-auto px-6 sm:px-8 pt-10 pb-4">
        <Link
          to="/instagram"
          className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-pink-600 transition-colors"
          data-testid="look-back-link"
        >
          <ArrowLeft className="w-4 h-4" />
          {t('Alle looks', 'All looks')}
        </Link>
      </div>

      <article className="max-w-4xl mx-auto px-6 sm:px-8 pb-20">
        {/* Hero image — natural aspect ratio, no cropping.
            Portrait Instagram photos display in full. */}
        <div className="relative rounded-3xl overflow-hidden bg-gray-100 mb-10 shadow-sm flex items-center justify-center">
          <img
            src={look.hero_image}
            alt={look.title}
            className="w-full h-auto max-h-[85vh] object-contain"
            loading="eager"
          />
        </div>

        {/* Title & intro */}
        <header className="text-center mb-10">
          <p className="text-xs uppercase tracking-[0.4em] text-pink-500 font-medium mb-4">
            {t('Shop de look', 'Shop the look')}
          </p>
          <h1 className="text-4xl sm:text-5xl font-light text-gray-900 leading-tight tracking-tight mb-6">
            {look.title}
          </h1>
          {intro && (
            <p className="text-lg text-gray-600 leading-relaxed max-w-2xl mx-auto mb-8">
              {intro}
            </p>
          )}

          {/* Primary CTA — sends the visitor to the products / affiliate link */}
          {look.products && look.products.length === 1 ? (
            <a
              href={look.products[0].affiliate_link}
              target="_blank"
              rel="noopener noreferrer sponsored"
              onClick={() =>
                trackAffiliateClick(look.products[0].brand_name, look.products[0].affiliate_link)
              }
              className="inline-flex items-center gap-2 bg-gray-900 text-white px-8 py-3.5 rounded-full text-sm font-medium hover:bg-pink-600 transition-colors"
              data-testid="look-primary-cta"
            >
              {t(`Shop deze look bij ${look.products[0].brand_name}`, `Shop this look at ${look.products[0].brand_name}`)}
              <ExternalLink className="w-4 h-4" />
            </a>
          ) : look.products && look.products.length > 1 ? (
            <a
              href="#look-products"
              className="inline-flex items-center gap-2 bg-gray-900 text-white px-8 py-3.5 rounded-full text-sm font-medium hover:bg-pink-600 transition-colors"
              data-testid="look-primary-cta"
            >
              {t(`Shop deze look (${look.products.length} items)`, `Shop this look (${look.products.length} items)`)}
              <ArrowDown className="w-4 h-4" />
            </a>
          ) : null}
        </header>

        {/* Products */}
        {look.products && look.products.length > 0 && (
          <section
            id="look-products"
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 scroll-mt-24"
            data-testid="look-products-grid"
          >
            {look.products.map((product, idx) => (
              <a
                key={idx}
                href={product.affiliate_link}
                target="_blank"
                rel="noopener noreferrer sponsored"
                className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-all"
                onClick={() => trackAffiliateClick(product.brand_name, product.affiliate_link)}
                data-testid={`look-product-${idx}`}
              >
                {product.image_url && (
                  <div className="aspect-[4/5] bg-gray-100 overflow-hidden">
                    <img
                      src={product.image_url}
                      alt={product.brand_name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                  </div>
                )}
                <div className="p-5">
                  <p className="text-xs uppercase tracking-[0.25em] text-pink-500 font-medium mb-2">
                    {product.brand_name}
                  </p>
                  {product.note && (
                    <p className="text-sm text-gray-600 leading-relaxed mb-3">{product.note}</p>
                  )}
                  <div className="inline-flex items-center gap-1.5 text-sm text-gray-900 font-medium group-hover:text-pink-600 transition-colors">
                    {t(`Shop bij ${product.brand_name}`, `Shop at ${product.brand_name}`)}
                    <ExternalLink className="w-3.5 h-3.5" />
                  </div>
                </div>
              </a>
            ))}
          </section>
        )}

        {/* Footer disclosure */}
        <p className="text-xs text-gray-400 text-center mt-14 max-w-xl mx-auto leading-relaxed">
          {t(
            'Deze pagina bevat affiliate links. Als je via een link aankoopt, ontvangen wij een kleine commissie — zonder extra kosten voor jou.',
            'This page contains affiliate links. If you buy through a link, we receive a small commission — at no extra cost to you.'
          )}
        </p>
      </article>
    </div>
  );
};

export default LookPage;
